import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  axiosStripe,
  axiosM2pay,
  billingInfoFunc,
  axiosGetAbonementInfo,
  axiosAbonementCreate,
  getParkingByUid,
  stripePaymentCheck,
} from "@API/axiosRequest";
import "./InfoCar.css";
import { DollarSvg } from "../../../assets/images/DollarSvg";
import { SubscriptionSvg } from "../../../assets/images/SubscriptionSvg";
import { PaymentInfo } from "./PaymentInfo/PaymentInfo";
import { SubscriptionInfo } from "./SubscriptionInfo/SubscriptionInfo";
import { currencySymbols } from "../../../static/static";
import { ParkingStatus } from "@Utils/enums/responseStatus";
import { calculateStayDuration, formatCheckInDate } from "@Utils/helpers/helpers";
import { useSelector } from "react-redux";
import { usePayment } from "../../../contexts/PaymentProvider";
import Loader from "@UI/kit/Loader/Loader";
import Button from "@UI/kit/Button/Button";
import FormContainer from "../../../UI/kit/FormContainer/FormContainer";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { NetworkContext } from "../../../contexts/NetworkContext";
import styles from "../EnterCarForm/enterCarForm.module.scss";
import { ReactComponent as Card } from "@Icons/payments/Card.svg";
import { ReactComponent as ArrowRight } from "@Icons/payments/ArrowRight.svg";
import { getPaymentProviders, getPaymentProvidersByParkingId, paymentCheck } from "../../../api/axiosRequest";
import { useModal } from "../../../contexts/ModalContext";
import { locale } from "dayjs";
import i18n from "i18next";

function InfoCar({ qr }) {
  const { isOnline } = useContext(NetworkContext);
  const { t } = useTranslation();
  const { setHandlePay } = usePayment();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const [info, setInfo] = useState(location.state?.info);
  const [plateNumber, setPlateNumber] = useState(location.state?.plateNumber);
  const [parkingUid, setParkingUid] = useState(params.parkingUid || location.state?.parkingUid);
  const [paymentMethod, setPaymentMethod] = useState(location.state?.paymentMethod);

  const [error, setError] = useState("");
  const [payLoading, setPayLoading] = useState(false);
  const [amount, setAmount] = useState(info?.sum || 0);
  const [type, setType] = useState(info?.result !== "error" ? params.rateType || "parking" : "subscription");
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [date, setDate] = useState(new Date());
  const [innerParkings, setInnerParkings] = useState([]);
  const [innerParkingId, setInnerParkingId] = useState(null);
  const [subscriptionAmount, setSubscriptionAmount] = useState(0);
  const [parkingInfo, setParkingInfo] = useState({ currency: "$", abonementEnabled: true });
  const initialLoad = useRef(true);

  const { region } = useSelector((state) => state.uaeCarForm);

  const [selectedSubscription, setSelectedSubscription] = useState("");

  const { openModal } = useModal();
  const [paymentProvider, setPaymentProvider] = useState(null);
  const [availablePaymentProviders, setAvailablePaymentProviders] = useState([]);
  const [paymentProviders, setPaymentProviders] = useState({});

  localStorage.setItem("parkingUid", params.parkingUid);

  const onChangePayment = (method) => {
    setPaymentProvider(method);
    localStorage.setItem("paymentMethod", method);
  };

  useEffect(() => {
    if (initialLoad.current) {
      setIsLoading(true);
      getPaymentProviders()
        .then((providers) => {
          const methods = providers.data.reduce((acc, provider) => {
            acc[provider?.providerName] = {
              img: provider?.iconUrl,
              text: provider?.providerName,
            };
            return acc;
          }, {});

          getPaymentProvidersByParkingId(parkingUid).then((res) => {
            const availableMethods = res.data.map((method) => ({
              paymentType: method.paymentType,
              link: method.link,
              ...methods[method.paymentType],
            }));

            if (availableMethods.length === 1) {
              setPaymentProvider(availableMethods[0].paymentType);
            }
            setAvailablePaymentProviders(availableMethods);
            setPaymentProviders(methods);
          });
        })
        .finally(() => setIsLoading(false));

      fetchParkingInfo();

      if (info.result === ParkingStatus.SUCCESS) {
        localStorage.setItem("inDate", info.inDate);
      }

      if (qr) {
        updateQrParams();
        fetchBillingInfo();
      }
      initialLoad.current = false;
    }
  }, []);

  useEffect(() => {
    if (type === "subscription") {
      localStorage.setItem("subscription", "true");
    } else {
      localStorage.removeItem("subscription");
    }
  }, [type]);

  useEffect(() => {
    setHandlePay(() =>
      info.sum || selectedSubscription.price
        ? type === "subscription"
          ? handleSubscriptionPayment
          : handlePayment
        : handleImitation,
    );
  }, [info, selectedSubscription, type]);

  useEffect(() => {
    fetchParkingInfo();
  }, [i18n.language]);

  const fetchSubscriptions = async (retryCount = 0) => {
    setIsLoading(true);
    try {
      const response = await axiosGetAbonementInfo(parkingUid, "en");
      if (response?.message !== "error") {
        setSubscriptions(response?.message.abonementTypes);
        setInnerParkings(response?.message.innerParkings);
        setInnerParkingId(response?.message.innerParkings[0].id);
      }
    } catch (err) {
      handleFetchError(err, retryCount, fetchSubscriptions);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchParkingInfo = async () => {
    try {
      setIsLoading(true);
      const res = await getParkingByUid(parkingUid);
      localStorage.setItem("parking", JSON.stringify(res.message));
      setParkingInfo({
        isEnabled: res?.message.abonementsEnabled,
        currency: res?.message.currency,
        name: res?.message.name,
      });
      if (res?.message.abonementsEnabled) {
        fetchSubscriptions();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBillingInfo = async () => {
    await billingInfoFunc(localStorage.getItem("billingToken"), params.plateNumber, params.parkingUid, 1).then((resp) =>
      setInfo(resp.result),
    );
  };

  const handlePayment = () => {
    if (paymentMethod === "portico") {
      handlePorticoPayment();
    } else if (paymentMethod === "stripe") {
      handleStripePayment();
    } else if (paymentMethod === "m2pay") {
      handleM2PayPayment();
    } else if (paymentMethod === "kaspi") {
      window.location.href = "https://kaspi.kz/pay/VBRParking";
    }
  };

  const handleSubscriptionPayment = async (resolve, reject) => {
    const formattedDate = formatDate(new Date(date));
    setPayLoading(true);

    try {
      await axiosAbonementCreate(plateNumber, parkingUid, innerParkingId, selectedSubscription.id, formattedDate);
    } catch (err) {
      reject(err);
    } finally {
      setPayLoading(false);
    }
  };

  const handlePorticoPayment = () => {
    localStorage.setItem("sum", info.sum);
    localStorage.setItem("plateNumber", plateNumber);
    localStorage.setItem("currency", info?.currency);

    navigate(`/parking/${parkingUid}/payment`, {
      state: {
        paymentInfo: {
          sum: info.sum,
          plateNumber: plateNumber,
          currency: info?.currency,
        },
      },
    });
  };

  const handleStripePayment = async () => {
    setPayLoading(true);
    try {
      const resp = await axiosStripe(
        plateNumber,
        amount,
        info?.currency,
        `${window.location.origin}/payment-success/${parkingUid}/${plateNumber}/`,
        `${window.location.origin}/payment-error`,
        parkingUid,
      );
      window.open(resp.data?.url, "_self");
    } finally {
      setPayLoading(false);
    }
  };

  const handleM2PayPayment = async () => {
    setPayLoading(true);
    try {
      await axiosM2pay(
        plateNumber,
        info.sum,
        `${window.location.origin}/payment-success/${parkingUid}/${plateNumber}/`,
        `${window.location.origin}/payment-error`,
        parkingUid,
      );
    } finally {
      setPayLoading(false);
    }
  };

  const handleFetchError = (err, retryCount, fetchFunction) => {
    if (err.response && err.response.status === 404) {
      if (retryCount < 5) {
        fetchFunction(retryCount + 1);
      } else {
        console.error("Retry limit reached, stopping further attempts.");
      }
    } else {
      console.error("An error occurred: ", err.message);
    }
  };

  const updateQrParams = () => {
    setPlateNumber(params.plateNumber);
    setParkingUid(params.parkingUid);
    setPaymentMethod(params.paymentMethod);
    localStorage.setItem("paymentMethod", params.paymentMethod);
  };

  const handleImitation = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      navigate(`/payment-success/${parkingUid}/${plateNumber}`);
    }, 1500);
  };

  const navigateToInfo = () => {
    if (type === "subscription") {
      handleSubscriptionPayment()
        .then(() => {
          localStorage.setItem(
            "subscriptionInfo",
            JSON.stringify({
              info,
              plateNumber,
              parkingUid,
              paymentMethod,
              selectedProvider: paymentProvider,
              subscriptionAmount,
              startDate: date,
              type,
            }),
          );

          navigate(`/parking/${parkingUid}/info/subscription/payment`, {
            state: {
              info,
              plateNumber,
              parkingUid,
              paymentMethod,
              selectedProvider: paymentProvider,
              subscriptionAmount,
              startDate: date,
              type,
            },
          });
        })
        .catch((err) => {
          toast.error(err);
        });
    } else {
      localStorage.setItem(
        "paymentInfo",
        JSON.stringify({
          info,
          plateNumber,
          parkingUid,
          paymentMethod,
          selectedProvider: paymentProvider,
          subscriptionAmount,
          type,
        }),
      );

      navigate(`/parking/${parkingUid}/info/payment`, {
        state: {
          info,
          plateNumber,
          parkingUid,
          paymentMethod,
          selectedProvider: paymentProvider,
          subscriptionAmount,
          type,
        },
      });
    }
  };

  if (!isOnline) {
    return (
      <FormContainer>
        <div className="flex w-full flex-col items-center space-y-3">
          <p className="text-center text-alertRed">{t("no_internet_connection")}</p>
        </div>
      </FormContainer>
    );
  }

  if (payLoading || isLoading) {
    return (
      <FormContainer>
        <div className="flex w-full items-center justify-center">
          <Loader />
        </div>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <div className="w-full">{renderSubscriptionContent()}</div>
    </FormContainer>
  );

  function renderSubscriptionContent() {
    return (
      <div className="flex w-full flex-col space-y-6">
        <PaymentOptions setType={setType} type={type} info={info} parkingInfo={parkingInfo} />
        {renderPaymentInfoOrSubscription()}
        {renderNextButton()}
      </div>
    );
  }

  function renderParkingSessionDetails() {
    return renderParkingInfoDetails();
  }

  function renderPaymentInfoOrSubscription() {
    return type === "parking" ? (
      <>
        <PaymentInfo info={info} parkingUid={parkingUid} plateNumber={plateNumber} />
        {!info?.leftFreeTimeMinutes && renderChoosePaymentMethod()}
      </>
    ) : (
      subscriptions?.length > 0 && (
        <SubscriptionInfo
          parkingInfo={parkingInfo}
          subscriptions={subscriptions}
          info={info}
          plateNumber={plateNumber}
          selectedSubscription={selectedSubscription}
          setSelectedSubscription={setSelectedSubscription}
          setDate={setDate}
          setInnerParkingId={setInnerParkingId}
          innerParkings={innerParkings}
          setSubscriptionAmount={setSubscriptionAmount}
          parkingUid={parkingUid}
        />
      )
    );
  }

  function renderChoosePaymentMethod() {
    return (
      <button
        className="mx-auto flex h-14 w-full max-w-[300px] items-center rounded-2xl border p-4"
        type="button"
        onClick={() => {
          availablePaymentProviders &&
            openModal("choosePayment", {
              onChange: onChangePayment,
              availablePaymentProviders,
            });
        }}
        disabled={availablePaymentProviders.length === 0}
      >
        {paymentProvider && (
          <div className="flex items-center space-x-3">
            <img src={paymentProviders[paymentProvider].img} alt="Payment Method" className="h-7" />
            <p>{paymentProviders[paymentProvider].text}</p>
          </div>
        )}

        {!paymentProvider && (
          <div className="flex items-center space-x-3">
            <Card />
            <p className="text-sm">{t("choose_payment_method")}</p>
            <ArrowRight />
          </div>
        )}
      </button>
    );
  }

  function renderNextButton() {
    return (
      <div className="flex w-full justify-center">
        {((info?.result !== 0 && type === "subscription") || info?.result === 0) && (
          <Button
            onClick={navigateToInfo}
            text={isLoading ? t("loading") : t("continue")}
            disabled={
              (!selectedSubscription && type === "subscription") || (info?.sum === 0 && type !== "subscription")
            }
          />
        )}
      </div>
    );
  }

  function renderParkingInfoDetails() {
    return (
      <>
        <div className="infoCar_name">{parkingInfo?.name}</div>
        <div className="infoCar_info">
          <span>{t("current_balance")}</span>
          <span>{formatCurrency(info?.currentBalance, info?.currency)}</span>
        </div>
        <div className="infoCar_info">
          <span>{t("license_plate")}</span>
          <span>{plateNumber}</span>
        </div>
        {info?.inDate && (
          <div className="infoCar_info">
            <span>{t("entrance_time")}</span>
            <span>{formatCheckInDate(info.inDate)}</span>
          </div>
        )}
        <div className="infoCar_info">
          <span>{t("parking_duration")}</span>
          <span>
            {info.durationInMinutes ? formatDuration(info.durationInMinutes) : calculateStayDuration(info.inDateISO)}
          </span>
        </div>
        <div className="infoCar_info highlighted">
          <span>{t("parking_fee")}</span>
          <span>{formatCurrency(info.sum, info?.currency)}</span>
        </div>
        {info.result === 0 && renderPaymentButton()}
      </>
    );
  }

  function renderPaymentButton() {
    return (
      <div className="flex w-full flex-col items-center">
        <Button onClick={handlePaymentOrImitation} text={payLoading ? t("loading") : t("pay")} />
      </div>
    );
  }

  function handlePaymentOrImitation() {
    if (info.sum) {
      handlePayment();
    } else {
      handleImitation();
    }
  }
}

const PaymentOptions = ({ type, setType, info, parkingInfo }) => {
  const abonementRestriction = localStorage.getItem("abonementRestriction");

  return (
    <div className="flex space-x-3">
      {info?.result !== "error" && (
        <PaymentOption type={type} setType={setType} optionType="parking" SvgComponent={DollarSvg} />
      )}
      {parkingInfo?.isEnabled && !abonementRestriction && (
        <PaymentOption type={type} setType={setType} optionType="subscription" SvgComponent={SubscriptionSvg} />
      )}
    </div>
  );
};

const PaymentOption = ({ type, setType, optionType, SvgComponent }) => {
  const isActive = type === optionType;
  const color = isActive ? "#FFF" : "#157FFF";
  const { t } = useTranslation();

  return (
    <div className={`payment-type ${isActive && "active"}`} onClick={() => setType(optionType)}>
      <SvgComponent color={color} />
      {optionType === "parking" ? t("pay_for_parking") : t("parking_permits")}
    </div>
  );
};

function formatCurrency(amount, currency) {
  return `${currencySymbols[currency?.toUpperCase()]}${amount?.toLocaleString("en").replace(/,/g, " ")}`;
}

function formatDuration(durationInMinutes) {
  const hours = Math.floor(Math.abs(durationInMinutes) / 60);
  const minutes = Math.abs(durationInMinutes) % 60;
  return `${hours} ${hours === 1 ? "hour" : "hours"} and ${minutes} minutes`;
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hour = date.getHours().toString().padStart(2, "0");
  const minute = date.getMinutes().toString().padStart(2, "0");
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

export default InfoCar;
